window.lessonData = {
    "greeting": [
        {
            "speaker": "Infinae",
            "text": "Welcome back. Today’s mission: spot linear functions fast — no tricks, just clean reasoning.",
            "audio": "Greetings/M1_Greet_1.mp3"
        },
        {
            "speaker": "Infinae",
            "text": "Think of it like speed-dating with graphs — you’ll know in seconds if it’s linear or not.",
            "audio": "Greetings/M1_Greet_2.mp3"
        },
        {
            "speaker": "UnvoicedNote",
            "text": "In live mode, Infinae makes sure students are ready before beginning."
        },
        {
            "speaker": "Infinae",
            "text": "You’ll see equations and graphs. Your job: decide if they’re linear and explain why.",
            "audio": "Greetings/M1_Greet_3.mp3"
        },
        {
            "speaker": "UnvoicedNote",
            "text": "In live mode, you can answer by voice or typing.",
            "audio": "Greetings/M1_Greet_4.mp3"
        }
    ],
    "concepts": [
        {
            "id": "A",
            "intro": [
                {
                    "speaker": "Infinae",
                    "text": "First, the core idea: A line is linear if it has the <b>same slope everywhere</b>.",
                    "audio": "Concept A/M1_CA_1.mp3",
                    "image": "M1_CA.png",
                },
                {
                    "speaker": "Infinae",
                    "text": `In equations, that’s <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mi>m</mi><mi>x</mi><mo>+</mo><mi>b</mi></math></span> — one x, power of 1, nothing fancy. That one x tells us it’s a linear line.`,
                    "audio": "Concept A/M1_CA_2.mp3",
                },
                {
                    "speaker": "Infinae",
                    "text": "On a graph, that’s a straight line with no curves and no surprises.",
                    "audio": "Concept A/M1_CA_3.mp3"
                },
                {
                    "speaker": "Infinae",
                    "text": "Try one. Pick the linear equation — I’ll ask “why” after.",
                    "audio": "Concept A/M1_CA_4.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, Infinae may ask follow-up questions to keep you thinking."
                },
                {
                    "speaker": "Infinae",
                    "text": "Which equation is linear?",
                    "audio": "Concept A/M1_CS1_Prompt.mp3"
                }
            ],
            "options": [
                {
                    "label": "A",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mn>2</mn><mi>x</mi><mo>+</mo><mn>3</mn></math></span>`,
                    "isCorrect": true,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": "Exactly. One x, power of 1. That’s linear.",
                                "audio": "Choice 1 Responses/M1_CS1_A_C1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": `Spot on. It fits <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mi>m</mi><mi>x</mi><mo>+</mo><mi>b</mi></math></span> perfectly.`,
                                "audio": "Choice 1 Responses/M1_CS1_A_C2.mp3"
                            }
                        ]
                    }
                },
                {
                    "label": "B",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><msup><mi>x</mi><mrow><mn>2</mn></mrow></msup><mo>−</mo><mn>4</mn></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Not linear. <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><msup><mi>x</mi><mrow><mn>2</mn></mrow></msup></math></span> curves the graph.`,
                                "audio": "Choice 1 Responses/M1_CS1_B_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Nope, quadratic means slope keeps changing.",
                                "audio": "Choice 1 Responses/M1_CS1_B_W2.mp3"
                            }
                        ],
                        "note": [
                            {
                                "speaker": "UnvoicedNote",
                                "text": "In live mode, Infinae can show helpful examples."
                            }
                        ]
                    }
                },
                {
                    "label": "C",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mfrac><mn>1</mn><mi>x</mi></mfrac></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Not quite. <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mfrac><mn>1</mn><mi>x</mi></mfrac></math></span> bends and never straightens.`,
                                "audio": "Choice 1 Responses/M1_CS1_C_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Nope, reciprocal graphs split and curve.",
                                "audio": "Choice 1 Responses/M1_CS1_C_W2.mp3"
                            }
                        ],
                        "note": [
                            {
                                "speaker": "UnvoicedNote",
                                "text": "In live mode, Infinae can show helpful examples."
                            }
                        ]
                    }
                },
                {
                    "label": "D",
                    "text": "The one that looks nicest.",
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": "Cute, but squinting doesn’t straighten curves.",
                                "audio": "Choice 1 Responses/M1_CS1_D_J1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Funny, but the graph’s still curved.",
                                "audio": "Choice 1 Responses/M1_CS1_D_J2.mp3"
                            }
                        ],
                        "note": [
                            {
                                "speaker": "UnvoicedNote",
                                "text": "In live mode, Infinae sometimes uses humor to keep students engaged."
                            }
                        ]
                    }
                },
            ],
            "reteach": [
                {
                    "speaker": "Infinae",
                    "text": `Linear means the same slope everywhere. If you see <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><msup><mi>x</mi><mrow><mn>2</mn></mrow></msup></math></span> or <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mfrac><mn>1</mn><mi>x</mi></mfrac></math></span> , slope changes — not linear.`,
                    "audio": "Choice 1 Responses/M1_CS1_Reteach.mp3"
                }
            ],
            "redo": [
                {
                    "speaker": "Infinae",
                    "text": "Let’s try that again — which equation is linear?",
                    "audio": "Choice 1 Responses/M1_CS1_Redo.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, Infinae may re-ask until the concept is clear."
                }
            ]
        },
        {
            "id": "B",
            "intro": [
                {
                    "speaker": "Infinae",
                    "text": "If you know a line is linear, two points are enough to determine the slope.",
                    "audio": "Concept B/M1_CB_1.mp3",
                    "image": "M1_CB.png"
                },
                {
                    "speaker": "Infinae",
                    "text": "Moreover, once you find the slope, you can write the whole equation if you know the y-intercept, or b value.",
                    "audio": "Concept B/M1_CB_2.mp3"
                },
                {
                    "speaker": "Infinae",
                    "text": `Look at the example question. The line is linear and it goes through points <b>(1,
                    2)</b> and <b>(3,
                    6)</b>. Slope is <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>m</mi><mo>=</mo><mfrac><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>6</mn><mo>−</mo><mn>2</mn></mrow></mstyle></mrow><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>3</mn><mo>−</mo><mn>1</mn></mrow></mstyle></mrow></mfrac><mo>=</mo><mfrac><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>4</mn></mrow></mstyle></mrow><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>2</mn></mrow></mstyle></mrow></mfrac><mo>=</mo><mn>2</mn></math></span>.`,
                    "audio": "Concept B/M1_CB_3.mp3"
                },
                {
                    "speaker": "Infinae",
                    "text": `Since the line goes through (0,
                    0), y-intercept is 0. Therefore, the equation of the line is: <span><math xmlns=\"http://www.w3.org/1998/Math/MathML\"><mi>y</mi><mo>=</mo><mn>2</mn><mi>x</mi><mo>+</mo><mn>0</mn></math></span> or <span><math xmlns=\"http://www.w3.org/1998/Math/MathML\"><mi>y</mi><mo>=</mo><mn>2</mn><mi>x</mi></math></span>. Done.`,"audio": "Concept B/M1_CB_4.mp3"
                },
                {
                    "speaker": "Infinae",
                    "text": "Your turn: A straight line goes through points <b>(2,5)</b> and <b>(4,9)</b>. What’s the slope?",
                    "audio": "Concept B/M1_CB_Prompt.mp3",
                    "image": "M1_CB_Q.png"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, students can ask for more time or let Infinae know they’re still working. Infinae will wait, but repeated requests or prolonged idling will be flagged as possible off-task behavior."
                }
            ],
            "options": [
                {
                    "label": "A",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>m</mi><mo>=</mo><mn>1</mn></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Not quite. Slope is rise over run. `,
                                "audio": "Concept B/M1_CB_A_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Close, you calculated rise/run wrong. Rise is vertical and run is horizontal.",
                                "audio": "Concept B/M1_CB_A_W2.mp3"
                            }
                        ]
                    }
                },
                {
                    "label": "B",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>m</mi><mo>=</mo><mn>2</mn></math></span>`,
                    "isCorrect": true,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Correct. Rise is 4, run is 2, slope is 2. `,
                                "audio": "Concept B/M1_CB_B_C1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": `Nice. Slope is <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mfrac><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>4</mn></mrow></mstyle></mrow><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>2</mn></mrow></mstyle></mrow></mfrac><mo>=</mo><mn>2</mn></math></span>.`,
                                "audio": "Concept B/M1_CB_B_C2.mp3"
                            }
                        ],
                    }
                },
                {
                    "label": "C",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>m</mi><mo>=</mo><mfrac><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>1</mn></mrow></mstyle></mrow><mrow><mpadded height="8.6pt" depth="3pt" width="0"><mrow></mrow></mpadded><mstyle displaystyle="false" scriptlevel="0"><mrow><mn>2</mn></mrow></mstyle></mrow></mfrac></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Not quite. You flipped rise/run.`,
                                "audio": "Concept B/M1_CB_C_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Nope, slope is rise over run, not the other way.",
                                "audio": "Concept B/M1_CB_C_W2.mp3"
                            }
                        ],
                    }
                },
                {
                    "label": "D",
                    "text": "No slope, too tired.",
                    "isCorrect": false,
                    "feedback": {
                        "note": [
                            {
                                "speaker": "UnvoicedNote",
                                "text": "In live mode, Infinae may steer the student back on track if jokes continue."
                            }
                        ]
                    }
                },
            ],
            "redo": [
                {
                    "speaker": "Infinae",
                    "text": "Try again. What’s the slope for between points <b>(2,5)</b> and <b>(4,9)</b>?",
                    "audio": "Concept B/M1_CB_Redo.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, Infinae guides students toward the right answer before moving on."
                }
            ]
        },
        {
            "id": "C",
            "intro": [
                {
                    "speaker": "Infinae",
                    "text": "Last check: equations can be written in many forms — still linear if the slope stays constant.",
                    "audio": "Concept C/M1_CC_1.mp3"
                },
                {
                    "speaker": "Infinae",
                    "text": `For examples: <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>−</mo><mn>3</mn><mo>=</mo><mn>2</mn><mo stretchy="false">(</mo><mi>x</mi><mo>+</mo><mn>1</mn><mo stretchy="false">)</mo></math></span> or <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mn>2</mn><mi>x</mi><mo>−</mo><mi>y</mi><mo>=</mo><mn>4</mn></math></span>. Different look, same slope rule.`,
                    "audio": "Concept C/M1_CC_2.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, Infinae can adjust the amount of algebra shown."
                },
                {
                    "speaker": "Infinae",
                    "text": "Which of these is <b>not</b> linear?",
                    "audio": "Concept C/M1_CC_Prompt.mp3",
                    "image": "M1_CC_Q.png",
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, students can ask for more time, and Infinae will wait."
                }
            ],
            "options": [
                {
                    "label": "A",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mo>−</mo><mn>5</mn><mi>x</mi><mo>+</mo><mn>7</mn></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": "Nope, that is still linear. The slope is −5",
                                "audio": "Concept C/M1_CC_A_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": `Not quite. It fits <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mi>m</mi><mi>x</mi><mo>+</mo><mi>b</mi></math></span>, so it’s linear.`,
                                "audio": "Concept C/M1_CC_A_W2.mp3"
                            }
                        ]
                    }
                },
                {
                    "label": "B",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mn>3</mn><mi>y</mi><mo>−</mo><mn>6</mn><mi>x</mi><mo>=</mo><mn>12</mn></math></span>`,
                    "isCorrect": false,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Nope, you can rearrange to <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mn>2</mn><mi>x</mi><mo>+</mo><mn>4</mn></math></span> , which is linear.`,
                                "audio": "Concept C/M1_CC_B_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Not quite. This is just in standard form, still linear.",
                                "audio": "Concept C/M1_CC_B_W2.mp3"
                            }
                        ],
                    }
                },
                {
                    "label": "C",
                    "text": `<span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><mi>y</mi><mo>=</mo><mn>4</mn><msup><mi>x</mi><mrow><mn>2</mn></mrow></msup><mo>+</mo><mn>1</mn></math></span>`,
                    "isCorrect": true,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": `Correct. <span><math xmlns="http://www.w3.org/1998/Math/MathML" display="block"><msup><mi>x</mi><mrow><mn>2</mn></mrow></msup></math></span> makes it quadratic, not linear.`,
                                "audio": "Concept C/M1_CC_C_C1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": "Exactly. Squared x makes it quadratic, so it’s out.",
                                "audio": "Concept C/M1_CC_C_C2.mp3"
                            }
                        ],
                    }
                },
            ],
            "reteach": [
                {
                    "speaker": "Infinae",
                    "text": `Linear means no exponents other than 1, no variables multiplied together. `,
                    "audio": "Concept C/M1_CC_Reteach.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, Infinae can handle unexpected answers appropriately."
                }
            ],
            "redo": [
                {
                    "speaker": "Infinae",
                    "text": "Try again — which of these is <b>not</b> linear?",
                    "audio": "Concept C/M1_CC_Redo.mp3"
                },
                {
                    "speaker": "UnvoicedNote",
                    "text": "In live mode, this re-ask would be open-ended, and Infinae would guide the student until correct before moving on."
                }
            ]
        },

        {
            "id": "Quick Fire",
            "intro": [
                {
                    "speaker": "Infinae",
                    "text": "Before we wrap — True or False: All linear equations have the same slope.",
                    "audio": "Quick Fire/M1_QF_Intro.mp3"
                },
            ],
            "options": [
                {
                    "label": "True",
                    "text": '',
                    "isCorrect": true,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": "Nope, slope depends on the equation. Linear just means slope is constant within one equation.",
                                "audio": "Quick Fire/M1_QF_A_W1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": `Not quite. Different lines can have different slopes. Constant slope only applies within a single equation.`,
                                "audio": "Quick Fire/M1_QF_A_W2.mp3"
                            }
                        ]
                    }
                },
                {
                    "label": "False",
                    "text": '',
                    "isCorrect": true,
                    "feedback": {
                        "infinae": [
                            {
                                "speaker": "Infinae",
                                "text": "Exactly. Slope varies between different lines, but stays constant within one.",
                                "audio": "Quick Fire/M1_QF_B_C1.mp3"
                            },
                            {
                                "speaker": "Infinae",
                                "text": `Correct. Each linear equation has its own slope.`,
                                "audio": "Quick Fire/M1_QF_B_C2.mp3"
                            }
                        ]
                    }
                },
            ],
        },
    ],
    "close": [
        {
            "speaker": "Infinae",
            "text": "And that’s it for today’s run. In live mode, we’d dive into tougher problems next. Keep that brain warmed up!",
            "audio": "Quick Fire/M1_QF_Close.mp3"
        },
        {
            "speaker": "UnvoicedNote",
            "text": "In live mode, students answer in their own words. Infinae adapts to their pace, keeps them engaged, and guides them back on track until the concept clicks",
        }
    ]
}